// Dual GraphQL(Apollo)+REST(Axios) client, matching the pattern of
// tmc-local-frontend/src/util/clients/index.js -- simplified for this smaller app
// (single backend, no separate auth service: this app owns its own login).
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import axios from 'axios';

import { configUrls } from './client-config';
import { getRawJwt } from '../jwt';
import { isSessionExpiredResponse, notifyUnauthorized } from './sessionExpiry';

const URLS = configUrls();

const httpLink = createHttpLink({
  uri: URLS.BACKEND_GQL,
});

// strips __typename from mutation input variables (Apollo adds it to query
// results for cache purposes, but the server rejects it back on mutations)
const cleanTypeName = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    const omitTypename = (key, value) => (key === '__typename' ? undefined : value);
    // eslint-disable-next-line no-param-reassign
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
  }
  return forward(operation);
});

const linkJwtHeader = setContext((_, { headers }) => {
  const token = getRawJwt();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const logGqlErrors = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      // eslint-disable-next-line no-console
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
  if (networkError) {
    // eslint-disable-next-line no-console
    console.error(`[Network error]: ${networkError}`);
    if (isSessionExpiredResponse(networkError.statusCode, URLS.BACKEND_GQL)) notifyUnauthorized();
  }
});

const backendLink = ApolloLink.from([
  cleanTypeName,
  linkJwtHeader,
  logGqlErrors,
  httpLink,
]);

const backendGql = new ApolloClient({
  link: backendLink,
  cache: new InMemoryCache(),
});

const backendRest = axios.create({
  baseURL: URLS.BACKEND_REST,
});

backendRest.interceptors.request.use((config) => {
  const token = getRawJwt();
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

backendRest.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && isSessionExpiredResponse(error.response.status, error.config && error.config.url)) {
      notifyUnauthorized();
    }
    return Promise.reject(error);
  },
);

export const clients = {
  direct: {
    backend: backendGql,
    backendRest,
  },
};

export default clients;
